import classnames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactResizeDetector from 'react-resize-detector';

import { BottomPanelContainer, useBottomPanel } from './BottomPanel';
import { CommonHeader, HeaderProps } from './CommonHeader';
import { SidebarContainer, useSidebar } from './Sidebar';

export type HeaderPropsType = HeaderProps;

export interface ContentProps extends React.PropsWithChildren {
  bottomPanelContent?: React.ReactNode;
  header?: HeaderProps;
  noMargin?: boolean;
  sidebarContent?: React.ReactNode;
}

export const Content: React.FC<ContentProps> = ({
  bottomPanelContent,
  header,
  children,
  noMargin = false,
  sidebarContent,
}) => {
  const { isSidebarOpen, isSidebarPinned } = useSidebar();
  const { isBottomPanelOpen } = useBottomPanel();

  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [bottomPanelHeight, setBottomPanelHeight] = useState<number>(0);

  const headerRef = useRef<HTMLDivElement>(null);
  const bottomPanelRef = useRef<HTMLDivElement>(null);

  const onResizeHandler = () => {
    setHeaderHeight(headerRef?.current?.offsetHeight ?? 0);
  };

  const onLayoutWidthResizeHandler = useCallback(() => {
    setBottomPanelHeight((bottomPanelContent && isBottomPanelOpen && bottomPanelRef?.current?.offsetHeight) || 0);
  }, [bottomPanelContent, isBottomPanelOpen]);

  useEffect(() => {
    /**
     * We need to trigger this handler whenever bottomPanelContent and isBottomPanelOpen changes, so that we have
     * accurately computed height of bottom panel present within styles (needed to compute height of sidebar)
     * */
    onLayoutWidthResizeHandler();
  }, [onLayoutWidthResizeHandler]);

  return (
    <>
      {/* TODO: update rendering on Header component using render props, https://jira.zoomint.com/browse/WFM-1478 */}
      {header && (
        <ReactResizeDetector handleHeight onResize={onResizeHandler}>
          <CommonHeader {...header} forwardedRef={headerRef} />
        </ReactResizeDetector>
      )}
      <ReactResizeDetector
        handleWidth={isSidebarOpen && !!bottomPanelContent && isBottomPanelOpen}
        onResize={onLayoutWidthResizeHandler}
        skipOnMount
      >
        <div
          className={classnames('content', {
            'content--sidebar-pinned': isSidebarOpen && sidebarContent && isSidebarPinned,
            'content--no-margin': noMargin,
          })}
          data-test-id="content"
          style={
            // https://github.com/frenic/csstype#what-should-i-do-when-i-get-type-errors
            {
              ['--header-height' as any]: `${headerHeight}px`,
              ['--bottom-panel-height' as any]: `${bottomPanelHeight}px`,
            }
          }
        >
          <main className="main-content" role="main">
            {children}
          </main>
          {isSidebarOpen && sidebarContent && <SidebarContainer>{sidebarContent}</SidebarContainer>}
          {isBottomPanelOpen && bottomPanelContent && (
            <BottomPanelContainer forwardedRef={bottomPanelRef}>{bottomPanelContent}</BottomPanelContainer>
          )}
        </div>
      </ReactResizeDetector>
    </>
  );
};
